import { AfterViewInit, Component, ElementRef, EventEmitter, Input, Output, ViewChild } from "@angular/core"
import Quill from "quill"
import BlotFormatter, { ImageSpec } from "@enzedonline/quill-blot-formatter2"

const FONTS = ["Arial", "Courier New", "Georgia", "Tahoma", "Times New Roman", "Verdana"]
const SIZES = ["10px", "12px", "14px", "16px", "18px", "24px", "32px"]

const fontStyle = Quill.import("attributors/style/font") as { whitelist: string[] }
const sizeStyle = Quill.import("attributors/style/size") as { whitelist: string[] }
fontStyle.whitelist = FONTS
sizeStyle.whitelist = SIZES

class HoverImageSpec extends ImageSpec {
    override init = (): void => {
        this.formatter.quill.root.addEventListener("click", this.onClick)
        this.formatter.quill.root.addEventListener("mouseover", this.onHover)
    }

    private onHover = (event: MouseEvent): void => {
        if ((this.formatter.quill.getSelection()?.length ?? 0) > 0) return
        this.onClick(event)
    }
}

Quill.register("modules/blotFormatter", BlotFormatter)
Quill.register("formats/align", Quill.import("attributors/style/align"), true)
Quill.register("formats/direction", Quill.import("attributors/style/direction"), true)
Quill.register("formats/font", fontStyle, true)
Quill.register("formats/size", sizeStyle, true)

const PLACEHOLDERS: { [lang: string]: string } = {
    hr: "Unesite tekst...",
    cz: "Zadejte text...",
    en: "Enter text...",
    fr: "Saisissez le texte...",
    de: "Text eingeben...",
    it: "Inserisci il testo...",
    pl: "Wprowadź tekst...",
    sk: "Zadajte text...",
    si: "Vnesite besedilo...",
}
const BUTTON_TITLES: { [ariaLabel: string]: string } = {
    bold: "Podebljano",
    italic: "Kurziv",
    underline: "Podcrtano",
    strike: "Precrtano",
    "script: sub": "Donji indeks",
    "script: super": "Gornji indeks",
    blockquote: "Citat",
    "code-block": "Blok koda",
    "list: ordered": "Numerirani popis",
    "list: bullet": "Popis s točkama",
    "list: check": "Popis sa zadacima",
    "indent: -1": "Smanji uvlaku",
    "indent: +1": "Povećaj uvlaku",
    "direction: rtl": "Smjer zdesna nalijevo",
    link: "Poveznica",
    image: "Slika",
    clean: "Ukloni formatiranje",
}

const PICKER_TITLES: { [format: string]: string } = {
    font: "Font",
    size: "Veličina teksta",
    header: "Stil naslova",
    color: "Boja teksta",
    background: "Boja pozadine",
    align: "Poravnanje",
}

type SnowDefaults = { DEFAULTS: { modules: { toolbar: { handlers: { link: (v: unknown) => void } } } } }
const stockLinkHandler = (Quill.import("themes/snow") as unknown as SnowDefaults).DEFAULTS.modules.toolbar.handlers.link

function wordAt(quill: Quill, index: number): { index: number; length: number } | null {
    const text = quill.getText()
    let start = index
    let end = index
    while (start > 0 && /\S/.test(text[start - 1])) start--
    while (end < text.length && /\S/.test(text[end])) end++
    return end > start ? { index: start, length: end - start } : null
}

function linkHandler(this: { quill: Quill }, value: unknown): void {
    const range = this.quill.getSelection()
    if (value && range && range.length === 0) {
        const word = wordAt(this.quill, range.index)
        if (word) this.quill.setSelection(word.index, word.length, "user")
    }
    stockLinkHandler.call(this, value)
}

const TOOLBAR = [
    [{ font: [false, ...FONTS] }, { size: [false, ...SIZES] }],
    [{ header: [1, 2, 3, 4, 5, 6, false] }],
    ["bold", "italic", "underline", "strike"],
    [{ color: [] }, { background: [] }],
    [{ script: "sub" }, { script: "super" }],
    ["blockquote", "code-block"],
    [{ list: "ordered" }, { list: "bullet" }, { list: "check" }],
    [{ indent: "-1" }, { indent: "+1" }],
    [{ direction: "rtl" }, { align: [] }],
    ["link", "image"],
    ["clean"],
]

@Component({
    selector: "valben-rich-text-editor",
    standalone: true,
    templateUrl: "./rich-text-editor.component.html",
})
export class RichTextEditorComponent implements AfterViewInit {
    @Input() set value(v: string) {
        this._value = v ?? ""
        if (this._quill && this._value !== this._emitted) this._seed()
    }
    @Input() key!: string
    @Output() signal: EventEmitter<{ key: string; value: string }> = new EventEmitter<{ key: string; value: string }>()

    @ViewChild("editor") private _editor!: ElementRef<HTMLDivElement>

    private _value: string = ""
    private _emitted: string = ""
    private _quill: Quill | undefined

    ngAfterViewInit(): void {
        this._quill = new Quill(this._editor.nativeElement, {
            theme: "snow",
            placeholder: PLACEHOLDERS[this.key.toLowerCase()] ?? PLACEHOLDERS["hr"],
            bounds: this._editor.nativeElement,
            modules: {
                toolbar: { container: TOOLBAR, handlers: { link: linkHandler } },
                blotFormatter: {
                    specs: [HoverImageSpec],
                    align: { allowAligning: false },
                    resize: { useRelativeSize: false, allowResizeModeChange: false, imageOversizeProtection: true },
                    image: { allowAltTitleEdit: false, allowCompressor: false, linkOptions: { allowLinkEdit: false } },
                },
            },
        })

        this._seed()
        this._labelPickers()
        this._quill.on("editor-change", () => this._syncLinkButton())
        this._syncLinkButton()

        this._quill.on("text-change", (_delta, _old, source) => {

            if (source !== "user") return

            this._emitted = this._quill!.getLength() <= 1 ? "" : this._quill!.getSemanticHTML()
            this.signal.emit({ key: this.key, value: this._emitted })
        })
    }

    private _seed(): void {
        const quill = this._quill!
        quill.setContents(quill.clipboard.convert({ html: this._value }), "silent")
        this._emitted = this._value
    }

    private _syncLinkButton(): void {
        const quill = this._quill!
        const toolbar = quill.getModule("toolbar") as { container: HTMLElement }
        const button = toolbar.container.querySelector<HTMLButtonElement>("button.ql-link")
        if (!button) return

        const range = quill.getSelection()
        const hasTarget = !!range && (range.length > 0 || !!wordAt(quill, range.index))
        button.disabled = !hasTarget
        button.title = hasTarget ? BUTTON_TITLES["link"] : "Poveznica (označite tekst)"
    }

    private _labelPickers(): void {
        const toolbar = this._quill!.getModule("toolbar") as { container: HTMLElement }
        toolbar.container.querySelectorAll(".ql-font .ql-picker-item, .ql-size .ql-picker-item").forEach(item => {
            const value = item.getAttribute("data-value")
            if (value) item.setAttribute("data-label", value)
        })

        toolbar.container.querySelectorAll("button").forEach(button => {
            const label = button.getAttribute("aria-label")
            if (label && BUTTON_TITLES[label]) button.title = BUTTON_TITLES[label]
        })

        for (const format of Object.keys(PICKER_TITLES)) {
            const label = toolbar.container.querySelector(`.ql-${format} .ql-picker-label`)
            if (label) (label as HTMLElement).title = PICKER_TITLES[format]
        }
    }
}
